import { george, pr, theme } from "aaa";
import { DynamicAmountTextField } from "components/common/DynamicAmountTextField";
import { InfoBoxWithBackground } from "components/common/InfoBoxWithBackground";
import { NativePaper } from "components/common/NativePaper";
import { NativeRaisedButton } from "components/common/NativeRaisedButton";
import { RadioButtonGroup } from "components/common/RadioButtonGroup";
import { ui } from "components/util/uiHelpers";
import { config } from "config";
import { observer } from "mobx-react";
import * as React from "react";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { pushRoute } from "stores/RouterFunctions";
import { history } from "stores/RouterStore";
import { amountToCurrencyString, amountToNumber, IAmount } from "types/amount";
import { GidSettingsRoutes } from "./GidSettingsRoutes";
import { GidSettingsStore } from "./GidSettingsStore";

const COLORS = {
    TEXT_PRIMARY: "#0A285C",
    TEXT_WHITE: "white",
} as const;

const getTextColor = () => ui.isDarkMode ? COLORS.TEXT_WHITE : COLORS.TEXT_PRIMARY;

type IProps = {
    store: GidSettingsStore
};

export const GidSettingsLimit = observer(({ store }: IProps) => {
    const isOnboardingLimitSite = history.location.pathname === GidSettingsRoutes.ONBOARDING_LIMIT;

    const handleChangeLimitSelection = (value: "noLimit" | "setLimit") => {
        store.selectedLimitOption = value;
    };

    const handleChangeLimit = (amount: IAmount) => {
        store.newLimit = amount;
    };

    const handleSubmit = async () => {
        try {
            await store.postLimitChange();
            pushRoute(isOnboardingLimitSite ? GidSettingsRoutes.ONBOARDING_LIMIT_SIGNING : GidSettingsRoutes.LIMIT_SIGNING);
        } catch (error) {
            pushRoute("/error");
        }
    };

    if (store.isLoading || (!store.selectedLimitOption && !store.isJuniorReadOnly)) {
        return <george.LoadingIndicator isLoading height={ui.windowHeight} />;
    }

    const buttonDisabled =
        amountToNumber(store.newLimit) === 0
        || amountToNumber(store.currentLimit) === amountToNumber(store.newLimit)
        || amountToNumber(store.newLimit) > config.GID_SETTINGS_MAX_DAILY_LIMIT;

    return (
        <>
            {/* since there is no homescreen currently there is no possibility to go back */}
            {/* {!isOnboardingLimitSite && (
                <TitleBar hideBackButton={externalInterfaceStore.isGeorgeApp}>
                    <pr.H1_16_FFFFFF>{ui.__("sidentitySettings.navbar.selectLimit")}</pr.H1_16_FFFFFF>
                </TitleBar>
            )} */}
            <div style={{ padding: theme.MARGIN }}>
                {isOnboardingLimitSite && (
                    <pr.P_32_TITLE style={{ fontWeight: 700 }}>
                        {ui.__("gidSettings.limit.title")}
                    </pr.P_32_TITLE>
                )}
                {store.isJuniorReadOnly ? (
                    <>
                        <pr.P_16_CONTENT style={{ color: getTextColor(), marginTop: isOnboardingLimitSite ? 16 : 0 }}>
                            {ui.__("gidSettings.limit.junior.text1")}
                        </pr.P_16_CONTENT>
                    </>
                ) : (
                    <>
                        {!isOnboardingLimitSite && (
                            <>
                                {store.currentLimit && amountToNumber(store.currentLimit) > 0 && (
                                    <pr.P_16_CONTENT style={{ color: getTextColor(), marginTop: 16 }}>
                                        {ui.__("gidSettings.limit.text.hasCurrentLimit")}
                                    </pr.P_16_CONTENT>
                                )}
                                {store.currentLimit && amountToNumber(store.currentLimit) === 0 && (
                                    <pr.P_16_CONTENT style={{ color: getTextColor(), marginTop: 16 }}>
                                        {ui.__("gidSettings.limit.text.hasNoCurrentLimit")}
                                    </pr.P_16_CONTENT>
                                )}
                            </>
                        )}
                        {isOnboardingLimitSite && (
                            <>
                                {store.defaultLimit && amountToNumber(store.currentLimit) > 0 && amountToNumber(store.currentLimit) <= amountToNumber(store.defaultLimit) && (
                                    <pr.P_16_CONTENT style={{ color: getTextColor(), marginTop: 16 }}>
                                        {ui.__("gidSettings.limit.text.limitBelowDefault")}
                                    </pr.P_16_CONTENT>
                                )}
                                {store.defaultLimit && amountToNumber(store.currentLimit) > amountToNumber(store.defaultLimit) && (
                                    <pr.P_16_CONTENT style={{ color: getTextColor(), marginTop: 16 }}>
                                        {ui.__("gidSettings.limit.text.limitAboveDefault")}
                                    </pr.P_16_CONTENT>
                                )}
                                {amountToNumber(store.currentLimit) === 0 && (
                                    <pr.P_16_CONTENT style={{ color: getTextColor(), marginTop: 16 }}>
                                        {ui.__("gidSettings.limit.text.noLimit")}
                                    </pr.P_16_CONTENT>
                                )}
                            </>
                        )}
                    </>
                )}
                {!store.isJuniorReadOnly && !store.isLimitMandatory && <NativePaper elevated={false} style={{ borderRadius: 16, marginTop: 16 }}>
                    <RadioButtonGroup
                        name="limitSelection"
                        options={[
                            { value: "noLimit", label: ui.__("gidSettings.limit.option.noLimit") },
                            { value: "setLimit", label: ui.__("gidSettings.limit.option.setLimit") },
                        ]}
                        onChange={handleChangeLimitSelection}
                        value={store.selectedLimitOption!}
                        showDivider
                        radioButtonStyle={{ margin: 0 }}
                        labelStyle={{
                            justifyContent: "space-between",
                            flexDirection: "row-reverse",
                            color: getTextColor(),
                            padding: "21px 16px"
                        }}
                    />
                </NativePaper>}
                {!store.isJuniorReadOnly && store.selectedLimitOption === "noLimit" && (
                    <InfoBoxWithBackground variant="error" iconSize={16} style={{ borderRadius: 10, marginTop: 16 }}>
                        <pr.P_14_CONTENT style={{ color: ui.isDarkMode ? theme.themeColors.d_grey_100 : COLORS.TEXT_PRIMARY }}>
                            {ui.__("gidSettings.limit.warning.noLimit", { limit: amountToCurrencyString(store.defaultLimit, true) })}
                        </pr.P_14_CONTENT>
                    </InfoBoxWithBackground>
                )}
                {(store.isJuniorReadOnly || store.selectedLimitOption === "setLimit") && (
                    <>
                        {!store.isJuniorReadOnly && store.defaultLimit && amountToNumber(store.currentLimit) > amountToNumber(store.defaultLimit) && (
                            <InfoBoxWithBackground variant="warning" iconSize={16} style={{ borderRadius: 10, marginTop: 16 }}>
                                <pr.P_14_CONTENT style={{ color: COLORS.TEXT_PRIMARY }}>
                                    {ui.__("gidSettings.limit.warning.limitAboveDefault", { limit: amountToCurrencyString(store.defaultLimit, true) })}
                                </pr.P_14_CONTENT>
                            </InfoBoxWithBackground>
                        )}
                        <NativePaper
                            elevated={false}
                            style={{ display: "flex", flexDirection: "column", alignItems: "center", borderRadius: 16, padding: "32px 40px", marginTop: 16 }}
                        >
                            <DynamicAmountTextField
                                onChange={handleChangeLimit}
                                value={store.isJuniorReadOnly ? store.currentLimit : store.newLimit}
                                maxAmount={config.GID_SETTINGS_MAX_DAILY_LIMIT}
                                disabled={store.isJuniorReadOnly}
                            />
                            <pr.P_12_CONTENT style={{ fontWeight: 600, marginTop: 16 }}>
                                EUR
                            </pr.P_12_CONTENT>
                        </NativePaper>
                        {!store.isJuniorReadOnly && (
                            <>
                                <pr.P_16_CONTENT style={{ marginTop: 16 }}>
                                    {ui.__("gidSettings.limit.validationHint")}
                                </pr.P_16_CONTENT>
                                <br />
                            </>
                        )}
                    </>
                )}
                <pr.P_16_CONTENT style={{ marginTop: store.selectedLimitOption === "noLimit" || store.isJuniorReadOnly ? 16 : 0 }}>
                    {store.isJuniorReadOnly ? ui.__("gidSettings.limit.junior.text2") : ui.__("gidSettings.limit.explanation")}
                </pr.P_16_CONTENT>
            </div>
            {!store.isJuniorReadOnly && (
                <div style={{ padding: theme.MARGIN, textAlign: "center" }}>
                    <NativeRaisedButton
                        className="theme-action-button-background-color"
                        style={{
                            height: 60,
                            minWidth: 163,
                            boxShadow: "none",
                            borderRadius: theme.BORDER_RADIUS,
                        }}
                        fullWidth={externalInterfaceStore.isGeorgeApp}
                        primary
                        labelStyle={{ color: COLORS.TEXT_WHITE, fontSize: 16, fontWeight: 600, textTransform: "none" }}
                        label={ui.__("button.continue")}
                        onClick={handleSubmit}
                        disabled={buttonDisabled}
                    />
                </div>
            )}
        </>
    );
});


// WEBPACK FOOTER //
// ./src/components/loginManagement/gidSettings/GidSettingsLimit.tsx